import React, { useEffect, useState } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { CreditCard, ExternalLink, Download, Loader, Mail, Sparkles, Check, X } from 'lucide-react';
import UsageMeter from '../components/UsageMeter';
import UsageForecast from '../components/UsageForecast';
import { formatDate as sharedFormatDate } from '../utils';
import PaymentFailureBanner from '../components/PaymentFailureBanner';

function StatusBadge({ status }) {
  const map = {
    active: { cls: 'bg-accent-green/15 text-accent-green', label: 'Aktiv' },
    past_due: { cls: 'bg-accent-red/15 text-accent-red', label: 'Forfalt' },
    cancelled: { cls: 'bg-slate-500/20 text-slate-400', label: 'Avsluttet' },
    canceled: { cls: 'bg-slate-500/20 text-slate-400', label: 'Avsluttet' },
    trialing: { cls: 'bg-accent-blue/15 text-accent-blue', label: 'Prøveperiode' },
    paid: { cls: 'bg-accent-green/15 text-accent-green', label: 'Betalt' },
    open: { cls: 'bg-accent-yellow/15 text-accent-yellow', label: 'Åpen' },
    void: { cls: 'bg-slate-500/20 text-slate-400', label: 'Annullert' },
    uncollectible: { cls: 'bg-accent-red/15 text-accent-red', label: 'Uinnkrevbar' },
  };
  const m = map[status] || { cls: 'bg-slate-500/20 text-slate-400', label: status || '—' };
  return <span className={`text-xs px-2 py-1 rounded ${m.cls}`}>{m.label}</span>;
}

const PLANS = [
  {
    key: 'Start',
    name: 'Start',
    priceNok: 1990,
    minutes: 100,
    tagline: 'For små klinikker og solopraksis',
    features: [
      '100 minutter inkludert',
      'AI-resepsjonist 24/7',
      'Norsk tale (Kari eller Lars)',
      'Samtalelogg og transkripsjoner',
      'E-postvarsler',
    ],
  },
  {
    key: 'Pro',
    name: 'Pro',
    priceNok: 5990,
    minutes: 400,
    tagline: 'For voksende virksomheter',
    features: [
      '400 minutter inkludert',
      'Alt i Start',
      'Webhook-integrasjon',
      'Avansert analyse og rapportering',
      'Prioritert support',
    ],
    recommended: true,
  },
  {
    key: 'Bedrift',
    name: 'Bedrift',
    priceNok: 9990,
    minutes: 1100,
    tagline: 'For klinikker med høyt volum',
    features: [
      '1100 minutter inkludert',
      'Alt i Pro',
      'Dedikert kundeansvarlig',
      'SLA på svartid',
      'Tilpasset onboarding',
    ],
  },
];

function PlanUpgradeModal({ currentPlan, onClose, onConfirm, loading }) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const selectedPlanObj = PLANS.find(p => p.key === selectedPlan);
  const currentPlanObj = PLANS.find(p => (p.key || '').toLowerCase() === (currentPlan || '').toLowerCase());

  if (confirming && selectedPlanObj) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
        onClick={() => !loading && setConfirming(false)}
        data-testid="plan-confirm-modal"
      >
        <div
          className="bg-navy-800 border border-navy-600 rounded-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="font-heading text-xl font-bold text-white mb-2">Bekreft planendring</h2>
          <p className="text-sm text-slate-400 mb-5">Du er i ferd med å endre abonnement. Vennligst bekreft.</p>

          <div className="bg-navy-900/50 border border-navy-600 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Plan</span>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-300">{currentPlanObj?.name || currentPlan || '—'}</span>
                <span className="text-slate-500">→</span>
                <span className="text-white font-semibold">{selectedPlanObj.name}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Ny månedspris</span>
              <span className="text-white font-heading font-bold text-lg">
                {selectedPlanObj.priceNok.toLocaleString('nb-NO')} NOK<span className="text-sm font-normal text-slate-400">/mnd</span>
              </span>
            </div>
          </div>

          <div className="text-xs text-slate-400 bg-navy-900/50 border border-navy-600 rounded-lg p-3 mb-5">
            Stripe håndterer prorert debitering/kreditering automatisk. Endringen trer i kraft umiddelbart.
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setConfirming(false)}
              disabled={loading}
              className="px-4 py-2.5 bg-navy-700 border border-navy-600 text-slate-300 text-sm font-medium rounded-lg hover:border-slate-500 hover:text-white transition-colors disabled:opacity-50"
            >
              Avbryt
            </button>
            <button
              onClick={() => onConfirm(selectedPlan)}
              disabled={loading}
              data-testid="plan-confirm-final"
              className="flex items-center gap-2 px-5 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {loading ? 'Behandler…' : 'Bekreft endring'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="plan-upgrade-modal"
    >
      <div
        className="bg-navy-800 border border-navy-600 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-navy-800 border-b border-navy-600 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-white">Velg abonnement</h2>
            <p className="text-sm text-slate-400 mt-0.5">Bytt plan når som helst — endringen trer i kraft med en gang (proratert).</p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-navy-700 transition-colors disabled:opacity-50"
            aria-label="Lukk"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
          {PLANS.map((plan) => {
            const isCurrent = (currentPlan || '').toLowerCase() === plan.key.toLowerCase();
            const isSelected = selectedPlan === plan.key;
            return (
              <button
                key={plan.key}
                type="button"
                onClick={() => !isCurrent && setSelectedPlan(plan.key)}
                disabled={isCurrent || loading}
                data-testid={`plan-card-${plan.key.toLowerCase()}`}
                className={`relative text-left p-5 rounded-lg border-2 transition-all ${
                  isCurrent
                    ? 'border-slate-600 bg-navy-900/50 cursor-not-allowed opacity-60'
                    : isSelected
                      ? 'border-accent-blue bg-accent-blue/5'
                      : 'border-navy-600 bg-navy-900/50 hover:border-accent-blue/50 cursor-pointer'
                }`}
              >
                {plan.recommended && !isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-blue text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Mest populær
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-slate-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Nåværende plan
                  </div>
                )}

                <div className="mb-3">
                  <h3 className="font-heading text-lg font-bold text-white">{plan.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{plan.tagline}</p>
                </div>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-3xl font-bold text-white">
                      {plan.priceNok.toLocaleString('nb-NO')}
                    </span>
                    <span className="text-sm text-slate-400">NOK/mnd</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{plan.minutes} min inkludert</p>
                </div>

                <ul className="space-y-2 text-sm text-slate-300">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} className="text-accent-green mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {isSelected && !isCurrent && (
                  <div className="mt-4 pt-4 border-t border-navy-600 text-xs text-accent-blue font-semibold">
                    ✓ Valgt
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="sticky bottom-0 bg-navy-800 border-t border-navy-600 px-6 py-4 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            Prisene er eks. mva. Proratert belastning eller kreditering beregnes automatisk.
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 bg-navy-700 border border-navy-600 text-slate-300 text-sm font-medium rounded-lg hover:border-slate-500 hover:text-white transition-colors disabled:opacity-50"
            >
              Avbryt
            </button>
            <button
              onClick={() => selectedPlan && setConfirming(true)}
              disabled={!selectedPlan || loading}
              data-testid="confirm-upgrade-button"
              className="flex items-center gap-2 px-5 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {loading ? 'Behandler…' : 'Fortsett'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BillingPage() {
  const toast = useToast();
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notAvailable, setNotAvailable] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const loadBilling = async () => {
    try {
      const subRes = await api.get('/api/billing/subscription');
      setSubscription(subRes.data);
      try {
        const invRes = await api.get('/api/billing/invoices');
        setInvoices(invRes.data.invoices || invRes.data || []);
      } catch { /* invoices optional */ }
    } catch (err) {
      if (err.response?.status === 404) {
        setNotAvailable(true);
      } else {
        toast.error('Kunne ikke laste faktureringsinformasjon');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();

    // Handle return from Stripe Checkout
    const params = new URLSearchParams(window.location.search);
    const upgradeStatus = params.get('upgrade');
    if (upgradeStatus === 'success') {
      toast.success('Abonnement oppdatert! Takk for kjøpet.');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (upgradeStatus === 'cancelled') {
      toast.info('Oppgradering avbrutt.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.get('/api/billing/portal-link');
      const url = data.url || data.portal_url || data.link;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Ingen portal-lenke mottatt');
      }
    } catch (err) {
      toast.error(err.response?.status === 404 ? 'Portal ikke tilgjengelig ennå' : 'Kunne ikke åpne betalingsportal');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgradeConfirm = async (planKey) => {
    setUpgradeLoading(true);
    try {
      const { data } = await api.post('/api/billing/upgrade', { plan: planKey });
      if (data.checkout_url) {
        // First-time subscription — redirect to Stripe hosted checkout
        window.location.href = data.checkout_url;
        return;
      }
      if (data.success) {
        toast.success(data.message || `Plan endret til ${data.plan || planKey}`);
        setShowUpgradeModal(false);
        await loadBilling();
      } else {
        toast.error('Kunne ikke oppgradere — prøv igjen eller kontakt support');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Kunne ikke oppgradere abonnementet');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const formatAmount = (amount, currency) => {
    if (amount == null) return '—';
    const cur = (currency || 'NOK').toUpperCase();
    return `${Number(amount).toLocaleString('nb-NO')} ${cur}`;
  };

  const formatDate = (d) => d ? sharedFormatDate(d) : '—';

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-navy-800 rounded w-48" />
      <div className="h-40 bg-navy-800 rounded-lg" />
      <div className="h-80 bg-navy-800 rounded-lg" />
    </div>
  );

  const currentPlan = subscription?.plan_name || subscription?.plan || 'Start';

  return (
    <div data-testid="billing-page">
      <PaymentFailureBanner hideUpgradeLink={true} />
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Fakturering</h1>
        <p className="text-slate-500 text-sm mt-1">Administrer abonnement og se faktureringshistorikk</p>
      </div>

      {notAvailable ? (
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-10 text-center">
          <CreditCard size={28} className="mx-auto text-slate-600 mb-3" />
          <h3 className="font-heading text-lg font-semibold text-white mb-2">Fakturering kommer snart</h3>
          <p className="text-sm text-slate-400 mb-4">Kontakt oss for å se abonnementsinformasjon.</p>
          <a
            href="mailto:support@airesepsjonen.no?subject=Sp%C3%B8rsm%C3%A5l%20om%20fakturering"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Mail size={14} /> Kontakt support
          </a>
        </div>
      ) : (
        <>
          {/* Starter upgrade CTA */}
          {['start', 'starter', 'Start'].includes(currentPlan.toLowerCase()) && subscription?.status === 'active' && (
            <div data-testid="starter-upgrade-cta" className="mb-6 flex items-center justify-between gap-4 bg-gradient-to-r from-accent-blue/15 to-purple-600/10 border border-accent-blue/30 rounded-lg px-5 py-4">
              <div className="flex items-center gap-3">
                <Sparkles size={18} className="text-accent-blue flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-white">Du er på Start-planen</p>
                  <p className="text-xs text-slate-400 mt-0.5">Oppgrader til Pro for 4× flere minutter, webhook-integrasjon og prioritert support.</p>
                </div>
              </div>
              <button
                onClick={() => setShowUpgradeModal(true)}
                data-testid="starter-cta-upgrade-button"
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Sparkles size={13} /> Oppgrader nå
              </button>
            </div>
          )}

          {/* Usage meter — minutes used vs plan limit */}
          <UsageMeter compact={false} />

          {/* Usage forecast — projected calls + overage estimate */}
          <UsageForecast />

          {/* Subscription plan card */}
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Nåværende abonnement</div>
                <h2 className="font-heading text-xl font-bold text-white">{currentPlan}</h2>
              </div>
              <StatusBadge status={subscription?.status} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Pris</div>
                <div className="text-lg font-heading font-bold text-white">
                  {subscription?.price != null ? `${Number(subscription.price).toLocaleString('nb-NO')} NOK` : (subscription?.price_nok != null ? `${Number(subscription.price_nok).toLocaleString('nb-NO')} NOK` : '—')}
                  <span className="text-sm text-slate-500 font-normal">/mnd</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Neste faktura</div>
                <div className="text-lg font-heading font-bold text-white">{formatDate(subscription?.next_invoice_date || subscription?.current_period_end)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Startet</div>
                <div className="text-lg font-heading font-bold text-white">{formatDate(subscription?.created_at || subscription?.started_at)}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-6 pt-5 border-t border-navy-600">
              <button
                onClick={openPortal}
                disabled={portalLoading}
                data-testid="billing-portal-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {portalLoading ? <Loader size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                Administrer betalingsmetode
              </button>
              <button
                onClick={() => setShowUpgradeModal(true)}
                data-testid="open-upgrade-modal-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 border border-navy-600 text-slate-300 text-sm font-medium rounded-lg hover:border-accent-blue hover:text-white transition-colors"
              >
                <Sparkles size={14} /> Oppgrader plan
              </button>
            </div>
          </div>

          {/* Invoices table */}
          <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-600">
              <h3 className="font-heading text-base font-semibold text-white">Fakturahistorikk</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-navy-600">
                    <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Dato</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Beløp</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Status</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 ? (
                    <tr><td colSpan="4" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-sm text-slate-400 font-medium">Ingen fakturaer ennå</span>
                        <span className="text-xs text-slate-500 max-w-md">Din første faktura vises her etter første betalingsperiode. Du kan laste ned PDF-er når de er tilgjengelige.</span>
                      </div>
                    </td></tr>
                  ) : invoices.map(inv => (
                    <tr key={inv.id || inv._id} className="border-b border-navy-600/50 hover:bg-navy-700/50 transition-colors">
                      <td className="px-4 py-3.5 text-sm text-slate-300">{formatDate(inv.date || inv.created_at || inv.issued_at)}</td>
                      <td className="px-4 py-3.5 text-sm text-white font-mono">{formatAmount(inv.amount ?? inv.total ?? inv.amount_nok, inv.currency)}</td>
                      <td className="px-4 py-3.5"><StatusBadge status={inv.status} /></td>
                      <td className="px-4 py-3.5 text-right">
                        {inv.pdf_url || inv.invoice_pdf ? (
                          <a
                            href={inv.pdf_url || inv.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-accent-blue hover:text-accent-blue-hover transition-colors"
                          >
                            <Download size={14} /> Last ned
                          </a>
                        ) : (
                          <span className="text-xs text-slate-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showUpgradeModal && (
        <PlanUpgradeModal
          currentPlan={currentPlan}
          loading={upgradeLoading}
          onClose={() => !upgradeLoading && setShowUpgradeModal(false)}
          onConfirm={handleUpgradeConfirm}
        />
      )}
    </div>
  );
}
